/**
 * Number and length formats people read (English only, SPEC §21 decision 19), shared by the app and, when it adopts
 * them, the extension side panel (decision 71). Rounding never shows an impossible value: 999,950 is "1.0M", not
 * "1000K", and 119.6 s is "2:00", not "1:60".
 */

const STEPS: Array<[number, string]> = [[1, ''], [1e3, 'K'], [1e6, 'M'], [1e9, 'B']];

/** "950", "1.2K", "12K", "1.0M", "15B"; "–" when missing. One decimal under 10 of a unit, none from 10 up. */
export function compactNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '–';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  let i = 0;
  while (i < STEPS.length - 1 && a >= STEPS[i + 1]![0]) i++;
  for (;; i++) {
    const [size, unit] = STEPS[i]!;
    const v = a / size;
    let text = unit === '' ? String(Math.round(v)) : v.toFixed(v >= 10 ? 0 : 1);
    // 9.96K rounds to "10.0K": shown as "10K", like every value from 10 up.
    if (unit && v < 10 && Number(text) >= 10) text = '10';
    // 999,950 rounds to "1000K": that is the next unit ("1.0M"). The largest unit just keeps counting.
    if (Number(text) >= 1000 && i < STEPS.length - 1) continue;
    return `${sign}${text}${unit}`;
  }
}

/** "45 s" under a minute, else "m:ss" ("1:05", "2:00"); "–" when missing. Seconds are rounded before splitting. */
export function formatDuration(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return '–';
  const total = Math.round(s);
  if (total < 60) return `${total} s`;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
